// eslint disabled because importing this library only stores the api on the document object, not as a direct export.
// eslint-disable-next-line no-unused-vars
import '@vp/pricing-context-module';
import pricingContextModuleHelpers from '@vp/pricing-context-module/helpers';
import {
  noticeError,
  trackPricingContextNotAvailable,
} from './newRelicTracking';
import { setBrowserCouponCodeProps } from '../types/PricingContextApi.types';

/*
 * A variable used as a queue for functions we need to run after the pricing
 * context is available on the page. Currently used in the setBrowserCouponCode
 * method.
 */
const pricingContextQueue: any[] = [];

declare global {
  interface Document {
    pcxtV3: any;
  }
}

/*
 * A variable that maintains the list of supported coupon error message key priorities,
 * the lower the number, the higher the priority
 */
const SUPPORTED_COUPON_ERROR_MESSAGE_KEY_PRIORITIES = {
  NotApplicable: 7,
  UnableToValidate: 6,
  Expired: 5,
  NotLiveYet: 4,
  RequiresMissingCustomerGroup: 3,
  ExceedsUsageLimit: 2,
  RequiresLoggedInShopper: 1,
};

const SUPPORTED_COUPON_ERROR_MESSAGE_KEYS = Object.keys(
  SUPPORTED_COUPON_ERROR_MESSAGE_KEY_PRIORITIES
);

/**
 * Initialization function of the Pricing Context Module using the module helper.
 * @param {*} merchantId The OM merchantId to use as an inbound parameter for initialization.
 * @param {*} country The OM Country code to use as an inbound parameter for initialization.
 *
 * both of these are configured in site-config
 * merchantId : pricingOptions: { merchantId }
 * country: country
 */
export const initializePricingContextModule = (
  merchantId: string,
  country: string
) =>
  new Promise(resolve => {
    pricingContextModuleHelpers.initializePricingContext(
      {
        merchantId,
        market: country,
        isDevelopmentMode: false, // process.env.NODE_ENV !== 'production',
      },
      // callback function from the pricing context module
      (pricingContext: any) => {
        // if we enqueued any functions, run them now that the pcxt module is initialized
        pricingContextQueue.forEach(() => {
          const fn = pricingContextQueue.shift();
          fn();
        });
        // resolve the promise, allows us to determine that pricing context is initialized
        resolve(pricingContext);
      }
    );
  });

/**
 * Apply any coupon code to the Pricing Context Module.
 * https://vistaprint.atlassian.net/wiki/spaces/PC/pages/20874846/Pricing+Context
 */
export async function setBrowserCouponCode({
  coupon = {},
  isAnonymousUser,
}: setBrowserCouponCodeProps) {
  if (coupon.type?.toLowerCase() === 'marketsale') {
    return;
  }

  if (!coupon.id) {
    coupon.id = '';
  }

  const pricingContextModule =
    typeof document !== 'undefined' && document && document.pcxtV3;

  if (!pricingContextModule || !pricingContextModule.isInitialized()) {
    // if the pricing context isn't available, queue up this call to setCouponCode, this
    // ensured the call isn't executed before the initialization of the pcxtv3 has happened
    pricingContextQueue.push(() => {
      pricingContextModule.setCouponCode(coupon.id);
    });
    trackPricingContextNotAvailable(
      new Error(
        `Pricing Context Module not available, will not be able to store coupon (${coupon}) details to the browser`
      )
    );
    return;
  }

  try {
    const couponAppliedResponse = await pricingContextModule.setCouponCode(
      coupon.id
    );
    // if the coupon failed to be applied...
    if (!couponAppliedResponse?.couponApplied) {
      const validationErrors = couponAppliedResponse?.validationErrors || [
        'NotApplicable',
      ];
      // sort the validation errors by priority
      validationErrors.sort(
        (
          a: keyof typeof SUPPORTED_COUPON_ERROR_MESSAGE_KEY_PRIORITIES,
          b: keyof typeof SUPPORTED_COUPON_ERROR_MESSAGE_KEY_PRIORITIES
        ) =>
          (SUPPORTED_COUPON_ERROR_MESSAGE_KEY_PRIORITIES[a] || 99) -
          (SUPPORTED_COUPON_ERROR_MESSAGE_KEY_PRIORITIES[b] || 99)
      );

      const firstValidationError =
        isAnonymousUser &&
        couponAppliedResponse?.validationErrors[0] ===
          'RequiresMissingCustomerGroup'
          ? 'RequiresLoggedInShopper'
          : couponAppliedResponse?.validationErrors[0];

      // if we don't know that message key, set it to the default message
      couponAppliedResponse.couponErrorMessageKey =
        SUPPORTED_COUPON_ERROR_MESSAGE_KEYS.includes(firstValidationError)
          ? firstValidationError
          : 'NotApplicable';
    }
    return couponAppliedResponse;
  } catch (error) {
    noticeError(error);
    console.error('Error syncing coupon to pricing context module', error);
  }
}

export function isVatInclusive() {
  const pricingContextModule =
    typeof document !== 'undefined' && document && document.pcxtV3;

  if (!pricingContextModule || !pricingContextModule.isInitialized()) {
    trackPricingContextNotAvailable(
      new Error('Call to isVatInclusive before PCTX initialiazed')
    );
    return true;
  }

  try {
    return pricingContextModule.isVatInclusive();
  } catch (error) {
    noticeError(error);
    console.warn(
      'Error fetching vat inclusivity data from pricing context module, defaulting to vat inclusive',
      error
    );
    return true;
  }
}
